import {ColumnDef} from '@tanstack/react-table';
import {DataTableColumnHeader} from "@/components/data-table/data-table-column-header.tsx";
import {Preference} from "@/types/Preference.ts";
import {AllCriteria} from "@/types/Criteria.ts";
import {getCriteriaValue, keyValueMapping} from "@/utils/criteria-helper.tsx";
import DataTableRowActions from "@/components/data-table/data-table-row-actions.tsx";
import {TFunction} from "i18next";

interface PreferencesColumnsProps {
    criteria: AllCriteria | undefined;
    t: TFunction;
}

const accessorFnForCriteria = (
    criteria: AllCriteria | undefined,
    criterion: string,
    value: any,
    t: TFunction
): string => {
    const keyMapping = keyValueMapping[criterion];
    const criterionLabel = keyMapping ? t(`criteria.${keyMapping.label}`) : criterion;
    const valueDisplay = keyMapping ? getCriteriaValue(criteria, criterion, value) : String(value ?? "");
    return valueDisplay ? `${criterionLabel} -> ${valueDisplay}` : criterionLabel;
};

export const getPreferencesColumns = ({criteria, t}: PreferencesColumnsProps): ColumnDef<Preference>[] => [
    {
        id: "matched",
        accessorFn: row => row.matched ? 'Yes' : 'No',
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="matched"/>
        ),
        filterFn: (row, id, value) => {
            return String(value) === row.getValue(id);
        },
        enableSorting: false,
    },
    {
        id: "name",
        accessorFn: row => `${row.mentee.user.firstName} ${row.mentee.user.lastName}`,
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="name"/>
        ),
        enableHiding: false,
    },
    {
        id: "email",
        accessorFn: row => row.mentee.user.email,
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="email" />
        ),
    },
    {
        id: "languages",
        accessorKey: "language.name",
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="languages"/>
        ),
        filterFn: (row, id, value) => {
            return value.includes(row.getValue(id));
        },
    },
    {
        id: "broad_criteria_1",
        accessorFn: (row) => accessorFnForCriteria(criteria, row.b1Key, row.b1Value, t),
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="broad_criteria_1"/>
        ),
        filterFn: (row, id, value) => {
            return String(row.getValue(id)).split('->')[0].trim() === String(value);
        },
    },
    {
        id: "broad_criteria_2",
        accessorFn: (row) => accessorFnForCriteria(criteria, row.b2Key, row.b2Value, t),
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="broad_criteria_2"/>
        ),
        filterFn: (row, id, value) => {
            return String(row.getValue(id)).split('->')[0].trim() === String(value);
        },
    },
    {
        id: "broad_criteria_3",
        accessorFn: (row) => accessorFnForCriteria(criteria, row.b3Key, row.b3Value, t),
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="broad_criteria_3"/>
        ),
        filterFn: (row, id, value) => {
            return String(row.getValue(id)).split('->')[0].trim() === String(value);
        },
    },
    {
        id: "specific_criteria_1",
        accessorFn: (row) => row.s1Key ? accessorFnForCriteria(criteria, row.s1Key, row.s1Value, t) : "",
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="specific_criteria_1"/>
        ),
        filterFn: (row, id, value) => {
            return String(row.getValue(id)).split('->')[0].trim() === String(value);
        },
    },
    {
        id: "specific_criteria_2",
        accessorFn: (row) => row.s2Key ? accessorFnForCriteria(criteria, row.s2Key, row.s2Value, t) : "",
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="specific_criteria_2"/>
        ),
        filterFn: (row, id, value) => {
            return String(row.getValue(id)).split('->')[0].trim() === String(value);
        },
    },
    {
        id: "specific_criteria_3",
        accessorFn: (row) => row.s3Key ? accessorFnForCriteria(criteria, row.s3Key, row.s3Value, t) : "",
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="specific_criteria_3"/>
        ),
        filterFn: (row, id, value) => {
            return String(row.getValue(id)).split('->')[0].trim() === String(value);
        },
    },
    {
        id: "specific_criteria_4",
        accessorFn: (row) => row.s4Key ? accessorFnForCriteria(criteria, row.s4Key, row.s4Value, t) : "",
        header: ({column}) => (
            <DataTableColumnHeader column={column} title="specific_criteria_4"/>
        ),
        filterFn: (row, id, value) => {
            return String(row.getValue(id)).split('->')[0].trim() === String(value);
        },
    },
    {
        id: "affinity_groups",
        accessorFn: (row) =>
            row.affinityGroups?.map((ag) => ag.affinityGroup.name).join(", ") ?? "",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="affinity_groups" />
        ),
        filterFn: (row, id, value) => {
            const data = String(row.getValue(id)).split(",").map((v) => v.trim());
            const filterValues = String(value).split(",").map((v) => v.trim());
            return filterValues.some((fv) => data.includes(fv));
        },
    },
    {
        id: 'actions',
        cell: ({ row }) => <DataTableRowActions row={row} viewProfileUrl={`/mentees/${row.original.menteeId}`} />,
    },
]
